import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import Markdown from "@/components/Markdown";
import { ShieldCheck, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

/**
 * Blocks the app with a modal until the logged-in user has accepted the
 * current version of the Terms & Conditions. Mounts inside AppShell so it
 * only runs for authenticated users.
 */
export default function TermsAcceptanceGate() {
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const [accepted, setAccepted] = useState(false);

  const { data: terms, isLoading } = useQuery({
    queryKey: ["legal-terms-public"],
    queryFn: async () => (await api.get("/legal/terms")).data,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const accept = useMutation({
    mutationFn: async () => (await api.post("/auth/accept-terms", { version: terms.version })).data,
    onSuccess: async () => {
      toast.success("¡Gracias! Términos aceptados.");
      await refresh();
      qc.invalidateQueries({ queryKey: ["legal-terms-public"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo registrar tu aceptación"),
  });

  // Don't show gate if user / terms not loaded yet.
  if (!user || isLoading || !terms) return null;

  // Already up-to-date with the live version → nothing to show.
  if ((user.terms_accepted_version || 0) >= terms.version) return null;

  const isUpdate = (user.terms_accepted_version || 0) > 0;

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        data-testid="terms-gate-dialog"
        className="max-w-2xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="h-9 w-9 rounded-xl bg-emerald-500/10 grid place-items-center">
              <ShieldCheck className="h-5 w-5 text-emerald-500" />
            </div>
            <DialogTitle className="text-xl">
              {isUpdate ? "Hemos actualizado los Términos y Condiciones" : "Acepta los Términos y Condiciones"}
            </DialogTitle>
          </div>
          <DialogDescription>
            {isUpdate
              ? `Esta es la nueva versión v${terms.version}. Léela y acéptala para continuar usando Sprintroll.`
              : "Antes de continuar, lee y acepta nuestros Términos y Condiciones."}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[42vh] overflow-y-auto kanban-scroll glass-card rounded-xl p-4 border border-white/40 dark:border-white/10">
          <div className="[&_h1]:font-display [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:mb-2 [&_h2]:font-display [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-1 [&_p]:my-2 [&_p]:text-sm [&_p]:leading-relaxed [&_li]:text-sm [&_li]:leading-relaxed">
            <Markdown>{terms.content}</Markdown>
          </div>
        </div>

        <div className="flex items-start gap-2.5 pt-2">
          <Checkbox
            id="terms-checkbox"
            data-testid="terms-gate-checkbox"
            checked={accepted}
            onCheckedChange={(v) => setAccepted(!!v)}
            className="mt-0.5"
          />
          <label htmlFor="terms-checkbox" className="text-sm leading-relaxed cursor-pointer select-none">
            He leído y acepto los{" "}
            <Link to="/terms" target="_blank" rel="noreferrer noopener" data-testid="terms-gate-link"
                  className="text-blue-600 dark:text-blue-400 underline underline-offset-2 inline-flex items-center gap-1">
              Términos y Condiciones <ExternalLink className="h-3 w-3" />
            </Link>{" "}
            de Sprintroll (versión {terms.version}).
          </label>
        </div>

        <DialogFooter className="mt-2">
          <Button
            data-testid="terms-gate-accept"
            disabled={!accepted || accept.isPending}
            onClick={() => accept.mutate()}
            className="w-full sm:w-auto"
          >
            {accept.isPending ? "Guardando…" : "Aceptar y continuar"}
          </Button>
        </DialogFooter>

        {isLoading && <Skeleton className="h-2 w-full" />}
      </DialogContent>
    </Dialog>
  );
}
